import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DOCUMENT_TYPE_LABELS } from "../mockData";
import type { DocumentType } from "../types";

export interface Filters {
  documentType: DocumentType | "all";
  counterparty: string;
  dateFrom: string;
  dateTo: string;
}

export const defaultFilters: Filters = {
  documentType: "all",
  counterparty: "",
  dateFrom: "",
  dateTo: "",
};

interface FilterBarProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

export function FilterBar({ filters, onFiltersChange }: FilterBarProps) {
  const hasActiveFilters =
    filters.documentType !== "all" ||
    filters.counterparty !== "" ||
    filters.dateFrom !== "" ||
    filters.dateTo !== "";

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <Select
        value={filters.documentType}
        onValueChange={(value) =>
          onFiltersChange({ ...filters, documentType: value as DocumentType | "all" })
        }
      >
        <SelectTrigger className="w-[180px] h-9 text-sm">
          <SelectValue placeholder="Document type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All types</SelectItem>
          {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          placeholder="Counterparty..."
          value={filters.counterparty}
          onChange={(e) =>
            onFiltersChange({ ...filters, counterparty: e.target.value })
          }
          className="pl-8 h-9 w-[180px] text-sm"
        />
      </div>

      <div className="flex items-center gap-1.5">
        <span className="text-xs text-neutral-500">From</span>
        <Input
          type="date"
          value={filters.dateFrom}
          onChange={(e) =>
            onFiltersChange({ ...filters, dateFrom: e.target.value })
          }
          className="h-9 w-[145px] text-sm"
        />
        <span className="text-xs text-neutral-500">to</span>
        <Input
          type="date"
          value={filters.dateTo}
          onChange={(e) =>
            onFiltersChange({ ...filters, dateTo: e.target.value })
          }
          className="h-9 w-[145px] text-sm"
        />
      </div>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onFiltersChange(defaultFilters)}
          className="h-9 text-sm text-neutral-500"
        >
          <X className="w-3.5 h-3.5 mr-1" />
          Clear
        </Button>
      )}
    </div>
  );
}
